import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { OpenDayDialog } from './components/OpenDayDialog';
import { CloseDayDialog } from './components/CloseDayDialog';
import { ClosureHistoryTable } from './components/ClosureHistoryTable';
import { DayEndClosureStatus, DayEndClosure } from '@/app/types/dayEndClosure';
import dayEndClosureService from '@/app/services/dayEndClosureService';
import { listSites, getSite } from '@/app/services/siteService';
import { Site } from '@/app/types';
import { usePreferenceStore, usePreferenceActions } from '@/app/store/preferenceStore';
import { toast } from 'sonner';
import { 
  Loader2, 
  Calendar, 
  DollarSign, 
  AlertCircle, 
  CheckCircle, 
  XCircle,
  Clock,
  IndianRupee,
  RefreshCw,
  Info
} from 'lucide-react';
import { format } from 'date-fns';

export default function DayEndClosurePage() {
  const [loading, setLoading] = useState(false);
  const [loadingSites, setLoadingSites] = useState(true);
  const [currentSite, setCurrentSite] = useState<Site | null>(null);
  const [status, setStatus] = useState<DayEndClosureStatus | null>(null);
  const [currentClosure, setCurrentClosure] = useState<DayEndClosure | null>(null);
  const [closureHistory, setClosureHistory] = useState<DayEndClosure[]>([]);
  const [openDayDialog, setOpenDayDialog] = useState(false);
  const [closeDayDialog, setCloseDayDialog] = useState(false);

  // Get default site from preferences
  const { defaultSiteId, isPreferencesLoaded } = usePreferenceStore();
  const { loadPreferences } = usePreferenceActions();

  // Load user preferences on mount
  useEffect(() => {
    if (!isPreferencesLoaded) {
      loadPreferences();
    }
  }, [isPreferencesLoaded, loadPreferences]);

  // Fetch site based on preferences or default
  useEffect(() => {
    const fetchSite = async () => {
      try {
        if (defaultSiteId) {
          // If user has a default site preference, use it
          const site = await getSite(defaultSiteId);
          setCurrentSite(site);
        } else {
          // Otherwise, fetch all sites and use the first one
          const response = await listSites({ limit: 10, skip: 0 });
          const sites = response.items || response;
          if (sites && sites.length > 0) {
            setCurrentSite(sites[0]);
          }
        }
      } catch (error) {
        // If fetching default site fails, fall back to fetching all sites
        try {
          const response = await listSites({ limit: 10, skip: 0 });
          const sites = response.items || response;
          if (sites && sites.length > 0) {
            setCurrentSite(sites[0]);
          }
        } catch (fallbackError) {
          toast.error('Failed to fetch sites');
        }
      } finally {
        setLoadingSites(false);
      }
    };

    if (isPreferencesLoaded) {
      fetchSite();
    }
  }, [defaultSiteId, isPreferencesLoaded]);

  const fetchStatus = async () => {
    if (!currentSite) return;
    
    setLoading(true);
    try {
      const statusData = await dayEndClosureService.getStatus(currentSite.id);
      setStatus(statusData);

      if (statusData.closure_id) {
        // Fetch the full closure details using the detail endpoint
        try {
          const fullClosure = await dayEndClosureService.getClosureById(statusData.closure_id);
          setCurrentClosure(fullClosure);
        } catch (error) {
          // If detail endpoint fails, fall back to list endpoint
          const history = await dayEndClosureService.getClosureHistory(currentSite.id, 0, 1);
          if (history.length > 0) {
            setCurrentClosure(history[0]);
          }
        }
      }
    } catch (error) {
      toast.error(dayEndClosureService.getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const fetchClosureHistory = async () => {
    if (!currentSite) return;

    try {
      const history = await dayEndClosureService.getClosureHistory(currentSite.id);
      setClosureHistory(history);
    } catch (error) {
      toast.error('Failed to fetch closure history');
    }
  };

  useEffect(() => {
    if (currentSite) {
      fetchStatus();
      fetchClosureHistory();
    }
  }, [currentSite]);

  const handleRefresh = () => {
    fetchStatus();
    fetchClosureHistory();
  };

  const getStatusDisplay = () => {
    if (!status) return null;

    const statusConfig = {
      'not_started': { 
        color: 'bg-gray-100 text-gray-800', 
        icon: Clock,
        title: 'Day Not Started',
        description: 'The business day has not been opened yet.'
      },
      'open': { 
        color: 'bg-success/10 text-success', 
        icon: CheckCircle,
        title: 'Day is Open',
        description: 'Business operations are in progress.'
      },
      'closing': { 
        color: 'bg-warning/10 text-warning', 
        icon: Clock,
        title: 'Day is Closing',
        description: 'Day-end closure is in progress.'
      },
      'closed': { 
        color: 'bg-destructive/10 text-destructive', 
        icon: XCircle,
        title: 'Day is Closed',
        description: 'The business day has been closed.'
      }
    };

    const config = statusConfig[status.status] || statusConfig.not_started;
    const Icon = config.icon;

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Current Day Status
            </span>
            <Button variant="ghost" size="sm" onClick={handleRefresh}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Business Date</p>
                <p className="text-lg font-semibold">
                  {format(new Date(status.current_date), 'EEEE, dd MMMM yyyy')}
                </p>
              </div>
              <Badge className={`${config.color} text-base px-3 py-1`}>
                <Icon className="w-4 h-4 mr-1" />
                {config.title}
              </Badge>
            </div>

            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>{config.title}</AlertTitle>
              <AlertDescription>
                {config.description}
                {status.message && <p className="mt-1">{status.message}</p>}
              </AlertDescription>
            </Alert>

            {currentClosure && status.status === 'open' && (
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Opening Balance</p>
                  <p className="text-lg font-semibold flex items-center gap-1">
                    <IndianRupee className="w-4 h-4" />
                    {parseFloat(currentClosure.opening_balance_physical || currentClosure.opening_balance_software).toLocaleString('en-IN')}
                  </p>
                  {currentClosure.opening_discrepancy && parseFloat(currentClosure.opening_discrepancy) !== 0 && (
                    <p className={`text-sm ${parseFloat(currentClosure.opening_discrepancy) < 0 ? 'text-destructive' : 'text-warning'}`}>
                      {parseFloat(currentClosure.opening_discrepancy) < 0 ? 'Shortage' : 'Overage'}: ₹{Math.abs(parseFloat(currentClosure.opening_discrepancy)).toLocaleString('en-IN')}
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Current Balance (Software)</p>
                  <p className="text-lg font-semibold flex items-center gap-1">
                    <IndianRupee className="w-4 h-4" />
                    {parseFloat(currentClosure.closing_balance_software || '0').toLocaleString('en-IN')}
                  </p>
                </div>
              </div>
            )}

            <div className="flex gap-2">
              {status.can_open_day && (
                <Button onClick={() => setOpenDayDialog(true)} className="flex-1">
                  Open Business Day
                </Button>
              )}
              {status.status === 'open' && currentClosure && (
                <Button onClick={() => setCloseDayDialog(true)} className="flex-1">
                  Close Business Day
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loadingSites) {
    return (
      <div className="container mx-auto py-6 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!currentSite) {
    return (
      <div className="container mx-auto py-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>No Site Available</AlertTitle>
          <AlertDescription>
            No site found for your account. Please contact your administrator.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading && !status) {
    return (
      <div className="container mx-auto py-6 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Day-End Cash Closure</h1>
          <p className="text-muted-foreground">Manage daily cash reconciliation and balance verification</p>
        </div>
      </div>

      <Tabs defaultValue="current" className="space-y-4">
        <TabsList>
          <TabsTrigger value="current">Current Status</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="current" className="space-y-4">
          {getStatusDisplay()}

          {currentClosure && currentClosure.status === 'closed' && (
            <Card>
              <CardHeader>
                <CardTitle>Today's Closure Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Opening Balance</p>
                    <p className="font-semibold flex items-center gap-1">
                      <IndianRupee className="w-3 h-3" />
                      {parseFloat(currentClosure.opening_balance_physical || '0').toLocaleString('en-IN')}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Closing Balance</p>
                    <p className="font-semibold flex items-center gap-1">
                      <IndianRupee className="w-3 h-3" />
                      {parseFloat(currentClosure.closing_balance_physical || '0').toLocaleString('en-IN')}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Total Discrepancy</p>
                    <p className={`font-semibold flex items-center gap-1 ${
                      parseFloat(currentClosure.closing_discrepancy || '0') < 0 ? 'text-destructive' : 
                      parseFloat(currentClosure.closing_discrepancy || '0') > 0 ? 'text-warning' : 'text-success'
                    }`}>
                      <IndianRupee className="w-3 h-3" />
                      {Math.abs(parseFloat(currentClosure.closing_discrepancy || '0')).toLocaleString('en-IN')}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Status</p>
                    <Badge variant={
                      currentClosure.closing_discrepancy && parseFloat(currentClosure.closing_discrepancy) === 0 
                        ? 'success' 
                        : 'destructive'
                    }>
                      {currentClosure.closing_discrepancy && parseFloat(currentClosure.closing_discrepancy) === 0 
                        ? 'Reconciled' 
                        : 'Not Reconciled'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="history">
          <ClosureHistoryTable closures={closureHistory} onRefresh={handleRefresh} />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <OpenDayDialog
        open={openDayDialog}
        onOpenChange={setOpenDayDialog}
        siteId={currentSite.id}
        onSuccess={handleRefresh}
      />

      {currentClosure && (
        <CloseDayDialog
          open={closeDayDialog}
          onOpenChange={setCloseDayDialog}
          closure={currentClosure}
          onSuccess={handleRefresh}
        />
      )}
    </div>
  );
}